
import React from 'react';
import { Button } from '$partials/ui';
import { useLocale } from '$contexts/locale.jsx';
import { IconChevronSprite } from '$partials/icons/index.js';
import Image from '$partials/media/image';
import { RichText } from '$partials/global/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleTextCards}
 */

const ModuleTextCards = ({
    bodyCopy,
    cta1Label,
    cta1Link,
    cta2Label,
    cta2Link,
    eyebrow,
    header,
    itemsCollection,
    isArticlePage,
}) => {
    const {
        formatURI,
        translate: t,
    } = useLocale();

    return (
        <section className={`relative overflow-hidden py-40${isArticlePage ? '' : ' md:py-80'}`}>
            <div className='layout-container layout-site-outer-padding'>

                <div className='bg-white'>
                    {eyebrow && (
                        <p className='type-style-eyebrow text-text-accent mb-12 md:mb-16'>
                            {eyebrow}
                        </p>
                    )}

                    {header && (
                        <h2 className={`${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} mb-10 md:mb-30`}>
                            {header}
                        </h2>
                    )}

                    {/* body copy */}
                    {bodyCopy && (
                        <p className='text-text-secondary type-style-body mt-16 sm:w-10/12 md:w-8/12'>
                            {bodyCopy}
                        </p>
                    )}


                    {/* Desktop */}
                    {itemsCollection?.items?.length > 0 && (
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-16 mt-24'>
                            {itemsCollection.items.map((item, i) =>
                                <div
                                    className={`flex flex-col items-start justify-between ${item?.textAlign === 'Center' ? 'text-center items-center' : 'text-left'} bg-surface-raised p-24 md:p-32 rounded`}
                                    key={i}>

                                    <div className=''>
                                        <div className='flex gap-8 mb-32 items-center'>
                                            {item?.eyebrow && (
                                                <p className='type-style-eyebrow text-text-accent'>
                                                    {item.eyebrow}
                                                </p>

                                            )}
                                            {item?.eyebrowIcon && (
                                                <Image
                                                    className='object-cover h-full w-full'
                                                    containerClassName='w-48 overflow-hidden'
                                                    src={item?.eyebrowIcon?.src}/>
                                            )}
                                        </div>
                                        {item?.heading && (
                                            <h3 className='type-style-4 text-text-primary mb-10'>
                                                {item.heading}
                                            </h3>
                                        )}
                                    </div>


                                    {item?.richTextBodyCopy?.json && (
                                        <RichText
                                            className='type-style-body text-text-secondary rich-text-text-cards'
                                            {...item.richTextBodyCopy} />
                                    )}

                                    {item?.ctaLink && item?.ctaLabel && (
                                        <Button
                                            as='a'
                                            className={`mt-40 block button ${item?.ctaStyle === 'Filled' ? 'button--primary' : 'button--outline'}`}
                                            href={formatURI(item.ctaLink)}>
                                            <span className='button__label--animate'>
                                                <span>{item.ctaLabel}</span>
                                            </span>
                                            {item?.ctaStyle === 'Filled' && (
                                                <span className='button__icon button__icon--animate'>
                                                    <IconChevronSprite />
                                                </span>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* cta buttons */}
                    {(cta1Link || cta2Link) && (
                        <div className='flex flex-wrap gap-8 mt-24'>
                            {cta1Link && (
                                <Button
                                    as='a'
                                    className='button button--primary'
                                    href={formatURI(cta1Link)}>
                                    <span className='button__label--animate'>
                                        <span>{cta1Label}</span>
                                    </span>
                                    <span className='button__icon button__icon--animate'>
                                        <IconChevronSprite />
                                    </span>
                                </Button>
                            )}
                            {cta2Link && (
                                <Button
                                    as='a'
                                    className='button button--secondary'
                                    href={formatURI(cta2Link)}>
                                    <span className='button__label--animate'>
                                        <span>{cta2Label}</span>
                                    </span>
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default ModuleTextCards;
